/**
 * Player Notes Modal
 *
 * Simple overlay for players to keep campaign notes.
 * Notes are stored per-character and auto-saved via characterStore.
 */

import { useState, useEffect, useRef } from 'react';
import { Icon } from './Icon';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

interface PlayerNotesModalProps {
  characterName: string;
  notes: string;
  onSave: (notes: string) => void;
  onClose: () => void;
}

export function PlayerNotesModal({ characterName, notes, onSave, onClose }: PlayerNotesModalProps) {
  const [text, setText] = useState(notes);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus textarea on open
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Auto-save on change (1s debounce)
  useEffect(() => {
    if (text === notes) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSave(text);
    }, 1000);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [text, notes, onSave]);

  // Save on close
  const handleClose = () => {
    if (text !== notes) onSave(text);
    onClose();
  };

  // Escape to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-md)',
          border: `2px solid ${GOLD_DIM}`,
          padding: 'var(--spacing-xl)',
          width: 600,
          maxWidth: '90vw',
          boxShadow: `0 20px 60px rgba(0, 0, 0, 0.6), 0 0 40px rgba(212, 175, 55, 0.1)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-md)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name="scroll" size={18} color={GOLD} />
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg)',
                fontFamily: 'var(--font-display)',
                fontWeight: 400,
                color: GOLD,
                letterSpacing: '0.5px',
              }}
            >
              {characterName}'s Notes
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: 'var(--color-text-secondary)',
            }}
          >
            <Icon name="close" size={16} />
          </button>
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Jot down quest notes, NPC names, clues, plans..."
          style={{
            width: '100%',
            height: 420,
            minHeight: 200,
            boxSizing: 'border-box',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            border: `1px solid rgba(100, 80, 120, 0.2)`,
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--spacing-md)',
            color: 'var(--color-text)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--font-size-sm)',
            lineHeight: 1.6,
            resize: 'vertical',
            outline: 'none',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = `${GOLD}40`;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'rgba(100, 80, 120, 0.2)';
          }}
        />

        {/* Footer hint */}
        <div
          style={{
            marginTop: 'var(--spacing-sm)',
            fontSize: 10,
            color: 'var(--color-text-secondary)',
            fontStyle: 'italic',
            opacity: 0.6,
          }}
        >
          Auto-saved
        </div>
      </div>
    </div>
  );
}
